import React, { useState, useEffect, useRef, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { ChevronLeft } from 'lucide-react';
import Header, { type HeaderProps } from '../../../../ui/src/components/Header/Header';
import Sidebar, { RAIL_W, MINI_W, type SidebarNavItem } from './Sidebar';
import Footer from './Footercomponent';
import V2PageWrapper from './V2PageWrapper';
import { ThemeProvider } from '../../providers/theme/ThemeProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useUser } from '../../providers/user/UserContext';
import { isPathBlocked, filterSidebarItems } from '../utils/roleAccess';
import './layout-styles.css';

/* ─── Route-set that shows the sidebar wrapper layout ────────── */
const WRAPPER_ROUTES = [
    '',             // dashboard "/"
    'patients-history',
    'scheduling',
    'voice-dictation',
    'help-page',
    'rad-ai',
    'notfoundstudy',
    'team',
    'medical-notes',
    'report',
    'support',
    'patient-register',
    'billing',
    'pathology',
    'radiology',
    'lab',
];

export interface LayoutProps {
    /** Sidebar menu items — same shape as legacy */
    menuItems: Array<{ title: string; path: string; icon: React.ElementType }>;
    /** Main content (route children) */
    children: ReactNode;
    /** Override header props */
    headerProps?: Partial<HeaderProps>;
    /** Header height (px) */
    headerHeight?: number;
    /** Show the footer */
    showFooter?: boolean;
    /** Custom footer content */
    footerContent?: ReactNode;
    /** QuickUpload component */
    quickUploadComponent?: ReactNode;
    /** Whether a file drag is in progress */
    isDraggingOver?: boolean;
    /** Custom className */
    className?: string;
}

const normalizePath = (path: string) => path.replace(/\/+/, '/').replace(/\/$/, '');

const Layout: React.FC<LayoutProps> = ({
    menuItems,
    children,
    headerProps = {},
    headerHeight: headerHeightProp,
    showFooter = true,
    footerContent,
    quickUploadComponent,
    isDraggingOver = false,
    className = '',
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const hasToggledRef = useRef(false);
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const isLargeDesktop = useMediaQuery('(min-width: 1280px)');
    const isMobile = useMediaQuery('(max-width: 767px)');
    const location = useLocation();
    const navigate = useNavigate();
    const { userRole, roleLoading } = useUser();

    // Compute header height — must match Header.tsx default (72px default, 60px compact/mobile)
    const headerHeight = headerHeightProp ?? (isMobile ? 60 : 72);

    // Should we show the wrapper (sidebar + header + footer)?
    const shouldShowWrapper = useMemo(() => {
        const path = normalizePath(location.pathname);
        const segment = path.split('/')[1] ?? '';
        return path === '' || WRAPPER_ROUTES.includes(segment);
    }, [location.pathname]);

    // Reset collapse when leaving wrapper routes
    useEffect(() => {
        if (!shouldShowWrapper) setIsCollapsed(false);
    }, [shouldShowWrapper]);

    // Auto-collapse on non-large-desktop (unless user explicitly toggled)
    useEffect(() => {
        if (!hasToggledRef.current) {
            // Large desktop (≥1280px): expanded; smaller: collapsed
            setIsCollapsed(!isLargeDesktop);
        }
    }, [isLargeDesktop]);

    useEffect(() => {
        if (isLargeDesktop) hasToggledRef.current = false;
    }, [isLargeDesktop]);

    const toggleSidebar = () => {
        setIsCollapsed((prev) => !prev);
        hasToggledRef.current = true;
    };

    async function handleSignOut() {
        try {
            await signOut();
            navigate('/');
        } catch (error) {
            console.log('error signing out: ', error);
        }
    }

    // Convert legacy menuItems shape to SidebarNavItem (recursive — supports nested sub-groups)
    const toSidebarSubItem = (c: any): any => ({
        key: c.key || c.path,
        label: c.title,
        path: c.path,
        icon: c.icon,
        children: c.children?.map(toSidebarSubItem),
    });

    const sidebarItems: SidebarNavItem[] = useMemo(
        () =>
            filterSidebarItems(menuItems, userRole).map((item: any, idx: number) => ({
                key: item.path || String(idx),
                label: item.title,
                icon: item.icon,
                path: item.path,
                children: item.children?.map(toSidebarSubItem),
            })),
        [menuItems, userRole]
    );

    // If not a wrapper route, render children directly (viewer, etc.)
    if (!shouldShowWrapper) return <>{children}</>;

    if (isPathBlocked(location.pathname, userRole))
        return <Navigate to="/" replace />;

    // On desktop (≥1024px): sidebar pushes content; on mobile/tablet: sidebar overlays
    const sidebarWidth = isDesktop ? (isCollapsed ? MINI_W : RAIL_W) : RAIL_W;
    const sidebarTransform = !isDesktop && isCollapsed ? 'translateX(-100%)' : 'translateX(0)';
    const contentMarginLeft = isDesktop ? sidebarWidth : 0;

    // Sidebar toggle button for header
    const sidebarToggle = (
        <button
            type="button"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 'var(--v2-radius-md)',
                border: '1px solid var(--v2-border-glass)',
                background: 'var(--v2-bg-glass)',
                color: 'var(--v2-text-primary)',
                cursor: 'pointer',
                transition: `all var(--v2-duration-fast) var(--v2-ease-calm)`,
                flexShrink: 0,
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-glass-strong)';
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-border-glass-strong)';
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--v2-bg-glass)';
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--v2-border-glass)';
            }}
        >
            <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
        </button>
    );

    return (
        <ThemeProvider>
            <div
                className={`flex h-screen overflow-hidden ${className}`.trim()}
                style={{
                    background: 'var(--v2-bg-main)',
                    color: 'var(--v2-text-primary)',
                }}
            >
                <aside
                    className="fixed left-0 top-0 z-40"
                    style={{
                        height: '100%',
                        width: sidebarWidth,
                        transform: sidebarTransform,
                        boxShadow: 'var(--v2-shadow-sidebar)',
                        transition: `transform var(--v2-duration-slow) var(--v2-ease-calm), width var(--v2-duration-slow) var(--v2-ease-calm)`,
                        overflow: 'hidden',
                    }}
                    aria-label="Primary navigation"
                >
                    <Sidebar
                        items={sidebarItems}
                        collapsed={isCollapsed}
                        setCollapsed={isDesktop ? setIsCollapsed : undefined}
                        onLogout={handleSignOut}
                        quickUploadComponent={quickUploadComponent}
                        isDraggingOver={isDraggingOver}
                        roleLoading={roleLoading}
                    />
                </aside>

                {isDesktop && createPortal(
                    <button
                        type="button"
                        onClick={() => setIsCollapsed(c => !c)}
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        style={{
                            position: 'fixed',
                            top: headerHeight - 14,
                            left: sidebarWidth - 13,
                            zIndex: 51,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: '1.5px solid var(--v2-border-card)',
                            background: 'var(--v2-bg-card)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px var(--v2-border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'var(--v2-text-muted)',
                            transition: `left var(--v2-duration-slow) var(--v2-ease-calm), all 0.18s ease`,
                        }}
                        onMouseEnter={(e) => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = 'var(--v2-primary-600)';
                            el.style.borderColor = 'var(--v2-primary-600)';
                            el.style.color = '#fff';
                            el.style.boxShadow = '0 3px 12px rgba(4,94,184,0.35)';
                        }}
                        onMouseLeave={(e) => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = 'var(--v2-bg-card)';
                            el.style.borderColor = 'var(--v2-border-card)';
                            el.style.color = 'var(--v2-text-muted)';
                            el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12), 0 0 0 1px var(--v2-border-subtle)';
                        }}
                    >
                        <ChevronLeft
                            style={{
                                width: 14, height: 14, flexShrink: 0,
                                transition: 'transform 0.28s ease',
                                transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    </button>,
                    document.body
                )}

                <div
                    className="fixed top-0 right-0 z-50"
                    style={{
                        left: isDesktop ? sidebarWidth : 0,
                        height: headerHeight,
                        transition: `left var(--v2-duration-slow) var(--v2-ease-calm)`,
                    }}
                >
                    <Header
                        {...headerProps}
                        showLogo={!isDesktop}
                        sidebarMenu={!isDesktop ? () => sidebarToggle : undefined}
                    />
                </div>
                <div
                    className="flex flex-1 min-w-0 flex-col overflow-hidden"
                    style={{
                        marginLeft: contentMarginLeft,
                        transition: `margin-left var(--v2-duration-slow) var(--v2-ease-calm)`,
                    }}
                >
                    {/* Main content */}
                    <main
                        className="flex-1 overflow-y-auto overflow-x-hidden v2-scrollbar"
                        style={{ paddingTop: headerHeight, scrollbarGutter: 'stable' }}
                    >
                        <V2PageWrapper>
                            {children}
                        </V2PageWrapper>
                    </main>

                    {/* {showFooter && <Footer>{footerContent}</Footer>} */}
                </div>
                {!isDesktop && !isCollapsed && (
                    <button
                        type="button"
                        aria-label="Close menu overlay"
                        onClick={() => setIsCollapsed(true)}
                        className="fixed inset-0 z-30"
                        style={{
                            background: 'rgba(0,0,0,0.55)',
                            backdropFilter: 'blur(4px)',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    />
                )}
            </div>
        </ThemeProvider>
    );
};

export default Layout;
